import { RegistroCta } from '@/features/auth/components/registro-cta';
import { motion } from 'motion/react';
import { ArrowRight, CheckCircle, Ship, DollarSign } from 'lucide-react';
import { ScrollReveal } from '@/components/motion/scroll-reveal';
import { ShowcaseBg, MockupWrapper } from './showcase-utils';

// ── Mockup internals ────────────────────────────────────────────

const costRows = [
  { concept: 'Valor FOB', amount: '$24,500' },
  { concept: 'Flete marítimo', amount: '$1,850' },
  { concept: 'Seguro', amount: '$245' },
  { concept: 'Arancel (15%)', amount: '$3,675' },
  { concept: 'Gastos despacho', amount: '$420' },
];

function ImportsMockup() {
  return (
    <>
      {/* Import summary header */}
      <div
        className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-4"
        style={{ transform: 'translateZ(20px)' }}
      >
        <div className="flex items-center justify-between mb-2">
          <div>
            <p className="text-[10px] text-gray-400 dark:text-gray-500 uppercase tracking-wide">
              Import ID
            </p>
            <p className="text-sm font-bold text-gray-900 dark:text-white">IMP-2024-0847</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-500/30">
              CIF
            </span>
            <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-500/30">
              En tr&aacute;nsito
            </span>
          </div>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Proveedor:{' '}
          <span className="font-medium text-gray-700 dark:text-gray-300">
            Shenzhen Electronics Co.
          </span>
        </p>
      </div>

      {/* Cost breakdown table */}
      <div
        className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden"
        style={{ transform: 'translateZ(14px)' }}
      >
        <div className="px-4 py-2.5 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/80">
          <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">
            Desglose de Costos
          </p>
        </div>
        {costRows.map((row, i) => (
          <motion.div
            key={row.concept}
            className="flex items-center justify-between px-4 py-2.5 border-b border-gray-50 dark:border-gray-700/50 last:border-0"
            initial={{ opacity: 0, x: -10 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.3 + i * 0.08, ease: 'easeOut' }}
          >
            <span className="text-[11px] text-gray-600 dark:text-gray-400">{row.concept}</span>
            <span className="text-xs font-semibold text-gray-800 dark:text-gray-200">
              {row.amount}
            </span>
          </motion.div>
        ))}
        {/* Total row */}
        <motion.div
          className="flex items-center justify-between px-4 py-3 bg-indigo-50 dark:bg-indigo-500/10 border-t border-indigo-100 dark:border-indigo-500/20"
          initial={{ opacity: 0, x: -10 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, delay: 0.3 + costRows.length * 0.08, ease: 'easeOut' }}
        >
          <span className="text-xs font-bold text-indigo-900 dark:text-indigo-200">
            Costo Total
          </span>
          <span className="text-sm font-bold text-indigo-700 dark:text-indigo-300">$30,690</span>
        </motion.div>
      </div>

      {/* Exchange rate alert */}
      <div
        className="flex items-center gap-3 rounded-xl border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 px-4 py-3"
        style={{ transform: 'translateZ(16px)' }}
      >
        <DollarSign className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
        <p className="text-[11px] text-amber-800 dark:text-amber-300 leading-snug">
          Tipo de cambio actualizado: <span className="font-semibold">1 USD = 6.92 BOB</span>{' '}
          <span className="text-amber-600 dark:text-amber-400">(+0.3%)</span>
        </p>
      </div>
    </>
  );
}

// ── Main export ─────────────────────────────────────────────────

export function ImportsSection() {
  return (
    <section id="imports" className="relative py-24 sm:py-32 overflow-hidden">
      <ShowcaseBg flip />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Mockup (left on desktop) */}
          <div className="order-2 lg:order-1">
            <MockupWrapper>
              <ImportsMockup />
            </MockupWrapper>
          </div>

          {/* Text (right on desktop) */}
          <ScrollReveal direction="right" className="space-y-6 order-1 lg:order-2">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-indigo-200 dark:border-indigo-500/30 bg-indigo-50 dark:bg-indigo-500/10 shadow-sm">
              <Ship className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />
              <span className="text-sm font-medium text-indigo-700 dark:text-indigo-300">
                Importaciones
              </span>
            </div>

            <div className="space-y-2">
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 dark:text-white leading-tight">
                De la factura del proveedor
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-blue-600">
                  al precio de venta, autom&aacute;tico.
                </span>
              </h2>
              <p className="text-gray-500 dark:text-gray-400 leading-relaxed">
                Registra cada importaci&oacute;n con sus costos desglosados por incoterm: flete,
                seguro, arancel, gastos de despacho. Prixia calcula el costo real de cada producto y
                actualiza tus precios autom&aacute;ticamente.
              </p>
            </div>

            <ul className="space-y-3">
              {[
                'Costos desglosados por incoterm (FOB, CIF, DDP)',
                'Tipo de cambio actualizado automáticamente',
                'Prorrateo de costos por línea de producto',
                'Historial completo de importaciones y costos',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <CheckCircle className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">{item}</span>
                </li>
              ))}
            </ul>

            <RegistroCta className="inline-flex items-center gap-2 text-sm font-semibold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors group">
              Explorar Importaciones
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </RegistroCta>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}
